/**
 * TicketsPanel + RevenuePanel — panneaux secondaires de la Vue portail.
 *
 * Extrait de AdminImmeublePortalMirror.jsx (Batch #14.221).
 */
import { AlertCircle, Home, TrendingUp, DollarSign, Building2 } from "lucide-react";
import { PanelTitle, EmptyState } from "./_shared";
import { fmtCAD as fmtMoney } from "@/lib/format";


function TicketsPanel({ tickets, buildingsById }) {
  return (
    <div>
      <PanelTitle icon={AlertCircle} label="Tickets ouverts" count={tickets.length} tone="rose" />
      {tickets.length === 0 ? (
        <EmptyState label="Aucun ticket ouvert." />
      ) : (
        <ul className="space-y-1.5">
          {tickets.map((t) => {
            const building = buildingsById[t.property_id];
            return (
              <li key={t.id}
                  className="flex items-start gap-3 p-2.5 rounded-lg border border-rose-200 bg-rose-50/40"
                  data-testid={`mirror-ticket-${t.id}`}>
                <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-800">{t.title || t.subject || "Ticket"}</div>
                  {t.description && (
                    <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{t.description}</div>
                  )}
                  {building?.name && (
                    <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {building.name}
                    </div>
                  )}
                </div>
                {t.priority && (
                  <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded h-fit ${
                    t.priority === "urgent" ? "bg-rose-100 text-rose-700" :
                    t.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                  }`}>
                    {t.priority}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RevenuePanel({ units, mode }) {
  // Batch #14.213 — Logique alignée avec backend : un logement génère du revenu
  // si occupé (au moins 1 locataire actif). Source priorité : lease > tenant override > unit
  const occupiedUnits = units
    .filter((u) => (u._tenants || []).length > 0)
    .map((u) => {
      let rent = Number(u._lease?.rent_monthly || 0);
      if (!rent) {
        rent = (u._tenants || []).reduce((s, t) => s + Number(t.rent_amount || 0), 0);
      }
      if (!rent) {
        rent = Number(u.rent_amount || 0);
      }
      return { ...u, _resolvedRent: rent };
    });
  const total = occupiedUnits.reduce((s, u) => s + u._resolvedRent, 0);
  const multiplier = mode === "annual" ? 12 : 1;
  const totalProjected = total * multiplier;

  return (
    <div>
      <PanelTitle
        icon={mode === "annual" ? TrendingUp : DollarSign}
        label={mode === "annual" ? "Revenu annuel projeté par logement" : "Revenu mensuel par logement"}
        count={occupiedUnits.length}
        tone={mode === "annual" ? "amber" : "emerald"}
      />
      {occupiedUnits.length === 0 ? (
        <EmptyState label="Aucun logement occupé." />
      ) : (
        <>
          <ul className="space-y-1.5">
            {occupiedUnits.map((u) => {
              const monthly = u._resolvedRent;
              const value = monthly * multiplier;
              const source = u._lease?.rent_monthly
                ? "Bail actif"
                : (u._tenants || []).some((t) => t.rent_amount)
                  ? "Override locataire"
                  : "Loyer logement";
              return (
                <li key={u.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-emerald-100 bg-emerald-50/40"
                    data-testid={`mirror-revenue-${u.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Home className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-800">
                      Apt {u.unit_label || u.id.slice(0, 6)}
                      <span className="ml-2 text-[10px] text-slate-500 font-normal">{u._building?.name || ""}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {source}{mode === "annual" ? ` · ${fmtMoney(monthly)}/mois × 12` : ""}
                    </div>
                  </div>
                  <span className="tabular-nums font-bold text-sm text-emerald-700">{fmtMoney(value)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Total {mode === "annual" ? "annuel projeté" : "mensuel"}
            </span>
            <span className="tabular-nums font-bold text-base text-emerald-700">{fmtMoney(totalProjected)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Composants utilitaires
// ════════════════════════════════════════════════════════════════════════════

export { TicketsPanel, RevenuePanel };
